import { useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { Grid, Typography, Box } from '@mui/material';
import {
  useAdditionalQuoteDocumentationTableInstance,
  AdditionalQuoteDocumentationTable,
} from 'quoting/quote-review/tables';
import { AdditionalQuoteDocumentationItem, QuoteDoc, QuoteDocument } from 'shared/types';
import { QuoteFormFileUploadField } from 'shared/controls';
import { useUserPermissions } from 'shared/hooks';
import { PERMISSIONS } from 'shared/constants';

interface Props {
  quote?: QuoteDocument;
}

export const QuoteDocumentation = ({ quote }: Props) => {
  const { watch } = useFormContext();
  const currentQuoteFilesToDelete = useMemo(() => {
    return (watch('quoteFilesToDelete') as QuoteDoc[]) || [];
  }, [watch]);

  const userPermissions = useUserPermissions();
  const hasFileAccessPermission: boolean =
    userPermissions.includes(PERMISSIONS['write']) || userPermissions.includes(PERMISSIONS['global.write']);

  const currentQuoteFilesToDeleteLength = currentQuoteFilesToDelete?.length;
  const tableData: AdditionalQuoteDocumentationItem[] = useMemo(() => {
    const quoteIdsMarkedForDeletion = !!currentQuoteFilesToDelete ? currentQuoteFilesToDelete.map(q => q.id) : [];
    const quoteFilesNotMarkedForDeletion = !!quote?.quoteDocumentation
      ? !!currentQuoteFilesToDeleteLength
        ? [...quote.quoteDocumentation.filter(q => !quoteIdsMarkedForDeletion.includes(q.id))]
        : [...quote?.quoteDocumentation]
      : [];
    return quoteFilesNotMarkedForDeletion;
  }, [quote?.quoteDocumentation, currentQuoteFilesToDelete, currentQuoteFilesToDeleteLength]);
  const additionalQuoteDocumentationTableInstance = useAdditionalQuoteDocumentationTableInstance(tableData);

  return (
    <Grid container>
      <Grid container spacing={{ xs: 1, md: 0 }}>
        <Grid xs={12} md={3} item>
          <Box sx={{ display: 'flex', flexFlow: 'row' }}>
            <Typography variant='subtitle1' sx={{ fontWeight: 'bold' }}>
              Quote Documentation
            </Typography>
          </Box>
        </Grid>
        <Grid xs={12} md={9} item>
          <Box sx={{ display: 'flex', flexFlow: 'row' }}>
            <Typography variant='subtitle1' sx={{ fontWeight: 'bold' }}>
              Previously Uploaded Files
            </Typography>
          </Box>
        </Grid>
        <Grid xs={12} md={3} item>
          <QuoteFormFileUploadField
            name={'files'}
            isMultiple={true}
            accept={'application/pdf'}
            disabled={!hasFileAccessPermission}
          />
        </Grid>
        <Grid xs={12} md={9} item>
          <AdditionalQuoteDocumentationTable instance={additionalQuoteDocumentationTableInstance} />
        </Grid>
      </Grid>
    </Grid>
  );
};
